import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSettings } from "./SettingsContext";

const CurrencyContext = createContext(null);
const STORAGE_KEY = "queensway_currency";

const SYMBOLS = {
  FCFA: "FCFA",
  GBP: "£",
  EUR: "€",
  USD: "$",
};

const PRECISION = {
  FCFA: 0,
  GBP: 0,
  EUR: 0,
  USD: 0,
};

export const SUPPORTED_CURRENCIES = ["FCFA", "GBP", "EUR", "USD"];

export function CurrencyProvider({ children }) {
  const { settings } = useSettings();
  const [currency, setCurrency] = useState(() => {
    if (typeof window === "undefined") return "FCFA";
    return localStorage.getItem(STORAGE_KEY) || "FCFA";
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, currency);
  }, [currency]);

  const rates = useMemo(
    () => ({
      FCFA: 1,
      GBP: Number(settings.fx_fcfa_to_gbp) || 0.0013,
      EUR: Number(settings.fx_fcfa_to_eur) || 0.00152,
      USD: Number(settings.fx_fcfa_to_usd) || 0.00165,
    }),
    [settings.fx_fcfa_to_gbp, settings.fx_fcfa_to_eur, settings.fx_fcfa_to_usd]
  );

  const format = (fcfaAmount, opts = {}) => {
    const n = Number(fcfaAmount || 0);
    if (n === 0 && !opts.showZero) return opts.zeroLabel || "On consultation";
    const r = rates[currency] || 1;
    const converted = n * r;
    if (currency === "FCFA") {
      return `${converted.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} FCFA`;
    }
    // Round nicely for foreign currencies — 5-unit increments under 100, else integer
    let display;
    if (converted < 100) display = (Math.round(converted * 2) / 2).toFixed(0);
    else display = Math.round(converted).toLocaleString("en-US");
    return `${SYMBOLS[currency] || ""}${display}`;
  };

  const value = useMemo(
    () => ({ currency, setCurrency, rates, format, symbol: SYMBOLS[currency] }),
    [currency, rates]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
}
